<div class="family container-lg py-3">
  <h1 class="family__title">{{ 'family.title' | translate }}</h1>
  <p class="family__intro">{{ 'family.intro' | translate }}</p>

  @if (loading() && !overview()) {
    <div class="text-center py-4"><c-spinner /></div>
  } @else if (blocked(); as reason) {
    <c-card class="family__card">
      <c-card-body>
        <p class="mb-0">{{ ('family.blocked.' + reason) | translate }}</p>
      </c-card-body>
    </c-card>
  } @else if (overview(); as o) {

    <!-- Counters: used = open + purchased; remaining = slots - used. -->
    <div class="family__counters" role="list">
      <div class="family__counter" role="listitem">
        <b>{{ o.used }}</b><span>{{ 'family.counters.used' | translate:{ slots: o.slots } }}</span>
      </div>
      <div class="family__counter" role="listitem">
        <b>{{ o.open }}</b><span>{{ 'family.counters.open' | translate }}</span>
      </div>
      <div class="family__counter" role="listitem">
        <b>{{ o.purchased }}</b><span>{{ 'family.counters.purchased' | translate }}</span>
      </div>
      <div class="family__counter family__counter--free" role="listitem">
        <b>{{ o.remaining }}</b><span>{{ 'family.counters.remaining' | translate:{ slots: o.slots } }}</span>
      </div>
    </div>

    <!-- Create an invitation -->
    <c-card class="family__card">
      <c-card-body>
        <h2 class="family__h">{{ 'family.create.title' | translate }}</h2>
        @if (o.remaining > 0) {
          <form class="family__form" (ngSubmit)="create()" novalidate>
            <label class="family__field">
              <span>{{ 'family.create.label' | translate }}</span>
              <input #labelInput class="form-control" type="text" name="label" [maxlength]="maxLabel"
                     [placeholder]="'family.create.label_placeholder' | translate"
                     [(ngModel)]="label" (ngModelChange)="onInput()" />
            </label>
            <label class="family__field">
              <span>{{ 'family.create.email' | translate }}</span>
              <input class="form-control" type="email" name="email" autocomplete="off"
                     [class.is-invalid]="!emailValid()"
                     [placeholder]="'family.create.email_placeholder' | translate"
                     [(ngModel)]="email" (ngModelChange)="onInput()" />
            </label>
            <p class="family__hint">{{ 'family.create.hint' | translate }}</p>
            <button cButton color="primary" type="submit" [disabled]="!canCreate()">
              {{ (creating() ? 'family.create.submitting' : 'family.create.submit') | translate }}
            </button>
          </form>
        } @else {
          <p class="mb-0">{{ 'family.create.none_left' | translate }}</p>
        }
        @if (createError(); as message) {
          <div class="alert alert-danger mt-3 mb-0" role="alert">{{ message }}</div>
        }
        @if (created(); as inv) {
          <div class="family__created mt-3" role="status">
            <p class="mb-1"><strong>{{ (inv.email ? 'family.create.done_mailed' : 'family.create.done') | translate:{ email: inv.email ?? '' } }}</strong></p>
            @if (linkOf(inv); as link) {
              <div class="family__link">
                <code>{{ link }}</code>
                <button cButton size="sm" variant="outline" type="button"
                        [color]="copiedId() === inv.id ? 'success' : 'primary'" (click)="copy(inv)">
                  {{ (copiedId() === inv.id ? 'family.copied' : 'family.copy_link') | translate }}
                </button>
              </div>
            }
          </div>
        }
      </c-card-body>
    </c-card>

    <!-- The invitations -->
    <c-card class="family__card">
      <c-card-body>
        <h2 class="family__h">{{ 'family.list.title' | translate }}</h2>
        @if (!invitations().length) {
          <p class="family__hint mb-0">{{ 'family.list.empty' | translate }}</p>
        }
        @for (inv of invitations(); track inv.id) {
          <div class="family__row">
            <div class="family__row-main">
              <div class="family__row-head">
                <strong>{{ inv.label || ('family.list.unnamed' | translate) }}</strong>
                <c-badge [color]="statusColor(inv.status)">{{ ('family.status.' + inv.status) | translate }}</c-badge>
              </div>
              @if (inv.email) { <div class="family__meta">{{ inv.email }}</div> }
              <div class="family__meta">
                {{ 'family.list.created' | translate:{ date: formatDate(inv.created_at) } }}
                @if (inv.purchased_at) {
                  · {{ 'family.list.purchased' | translate:{ date: formatDate(inv.purchased_at) } }}
                } @else if (inv.accepted_at && inv.status === 'accepted') {
                  · {{ 'family.list.accepted' | translate:{ date: formatDate(inv.accepted_at) } }}
                }
                @if (inv.expires_at && (inv.status === 'pending' || inv.status === 'accepted')) {
                  · {{ 'family.list.expires' | translate:{ date: formatDate(inv.expires_at) } }}
                }
              </div>
              @if (rowError()?.id === inv.id) { <div class="text-danger small">{{ rowError()?.message }}</div> }
              @if (rowNotice()?.id === inv.id) { <div class="text-success small">{{ rowNotice()?.message }}</div> }
            </div>
            <div class="family__row-actions">
              @if (hasLink(inv)) {
                <button cButton size="sm" variant="outline" type="button"
                        [color]="copiedId() === inv.id ? 'success' : 'primary'" (click)="copy(inv)">
                  {{ (copiedId() === inv.id ? 'family.copied' : 'family.copy_link') | translate }}
                </button>
              }
              @if (inv.email && (inv.status === 'pending' || inv.status === 'accepted')) {
                <button cButton size="sm" variant="ghost" color="secondary" type="button"
                        [disabled]="busyId() === inv.id" (click)="resend(inv)">
                  {{ 'family.resend' | translate }}
                </button>
              }
              @if (inv.revocable) {
                <button cButton size="sm" variant="ghost" color="danger" type="button"
                        [disabled]="busyId() === inv.id" (click)="revoke(inv)">
                  {{ 'family.revoke' | translate }}
                </button>
              }
            </div>
          </div>
        }
      </c-card-body>
    </c-card>

    <!-- What the family member gets -->
    <c-card class="family__card">
      <c-card-body>
        <h2 class="family__h">{{ 'gh_product.dna_family_special.name' | translate }}</h2>
        <p class="family__price"><b>${{ price() | number:'1.0-2' }}</b> {{ 'family.product.one_time' | translate }}</p>
        <p>{{ 'family.product.what' | translate }}</p>
        <div class="alert alert-warning" role="note">
          <strong>{{ 'gh_product.dna_family_special.no_guarantee' | translate }}</strong>
          <div class="small">{{ 'membership.family.notice' | translate }}</div>
        </div>
        <p class="family__hint">{{ 'family.product.privacy' | translate }}</p>
        <p class="family__hint mb-0">{{ 'membership.bundle.contracting_party' | translate }}</p>
      </c-card-body>
    </c-card>
  }
</div>
